import { useMemo, useState, useEffect } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { WordCard } from '@/components/vocabulary/WordCard'
import {
  groupWordsByDateAdded,
  groupWordsByLetter,
  dateSectionId,
  letterSectionId,
} from '@/lib/vocabularyGrouping'
import type { VocabularySortMode } from '@/lib/vocabularySortStorage'
import type { UserWordSummary } from '@/types/user'
import type { WordStatus } from '@/types/common'

interface VocabularyWordListProps {
  words: UserWordSummary[]
  statusFilter: WordStatus | ''
  sortMode?: VocabularySortMode
  onDelete?: (id: string) => void
  deleteAriaLabel?: string
  searchQuery?: string
  onSectionsChange?: (sections: string[]) => void
  enableDrag?: boolean
  onAddToCollection?: (word: UserWordSummary) => void
  highlightWordId?: string | null
}

function WordGroupSection({
  label,
  sectionId,
  words,
  onDelete,
  deleteAriaLabel,
  searchQuery,
  enableDrag,
  onAddToCollection,
  highlightWordId,
}: {
  label: string
  sectionId: string
  words: UserWordSummary[]
  onDelete?: (id: string) => void
  deleteAriaLabel?: string
  searchQuery?: string
  enableDrag?: boolean
  onAddToCollection?: (word: UserWordSummary) => void
  highlightWordId?: string | null
}) {
  return (
    <section id={sectionId} className="mb-6 scroll-mt-24">
      <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 bg-background/95 py-1 backdrop-blur-sm">
        <span className="rounded-md bg-primary/10 px-2 py-1 text-sm font-semibold text-primary">
          {label}
        </span>
        <span className="text-xs text-muted-foreground">{words.length}</span>
      </div>
      <div className="space-y-2 border-l-2 border-border/60 pl-3">
        {words.map((w) => (
          <WordCard
            key={w.id}
            userWord={w}
            onDelete={onDelete}
            deleteAriaLabel={deleteAriaLabel}
            draggable={enableDrag}
            onAddToCollection={onAddToCollection}
            searchQuery={searchQuery}
            variant="list"
            highlighted={highlightWordId === w.id}
          />
        ))}
      </div>
    </section>
  )
}

export function VocabularyWordList({
  words,
  statusFilter,
  sortMode = 'alphabetical',
  onDelete,
  deleteAriaLabel,
  searchQuery,
  onSectionsChange,
  enableDrag = false,
  onAddToCollection,
  highlightWordId = null,
}: VocabularyWordListProps) {
  const [masteredOpen, setMasteredOpen] = useState(false)

  const { active, mastered } = useMemo(() => {
    const activeWords: UserWordSummary[] = []
    const masteredWords: UserWordSummary[] = []
    for (const w of words) {
      if (w.status === 'MASTERED') masteredWords.push(w)
      else activeWords.push(w)
    }
    return { active: activeWords, mastered: masteredWords }
  }, [words])

  const showMasteredSection = statusFilter !== 'MASTERED' && mastered.length > 0
  const displayActive = statusFilter === 'MASTERED' ? [] : active

  const activeGroups = useMemo(() => {
    if (sortMode === 'dateAdded') {
      return groupWordsByDateAdded(displayActive)
    }
    return groupWordsByLetter(displayActive)
  }, [displayActive, sortMode])

  const sections = useMemo(() => {
    if (statusFilter === 'MASTERED') {
      if (sortMode === 'dateAdded') {
        return [...groupWordsByDateAdded(mastered).keys()]
      }
      return [...groupWordsByLetter(mastered).keys()]
    }
    return [...activeGroups.keys()]
  }, [statusFilter, mastered, activeGroups, sortMode])

  useEffect(() => {
    onSectionsChange?.(sections)
  }, [sections, onSectionsChange])

  const renderGroups = (groups: Map<string, UserWordSummary[]>) =>
    Array.from(groups.entries()).map(([label, groupWords]) => (
      <WordGroupSection
        key={label}
        label={label}
        sectionId={sortMode === 'dateAdded' ? dateSectionId(label) : letterSectionId(label)}
        words={groupWords}
        onDelete={onDelete}
        deleteAriaLabel={deleteAriaLabel}
        searchQuery={searchQuery}
        enableDrag={enableDrag}
        onAddToCollection={onAddToCollection}
        highlightWordId={highlightWordId}
      />
    ))

  if (statusFilter === 'MASTERED') {
    const groups = sortMode === 'dateAdded' ? groupWordsByDateAdded(mastered) : groupWordsByLetter(mastered)
    return <div>{renderGroups(groups)}</div>
  }

  return (
    <div>
      {renderGroups(activeGroups)}

      {showMasteredSection && (
        <section className="mt-8 rounded-xl border border-dashed border-border/80 bg-muted/20">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setMasteredOpen((o) => !o)}
          >
            {masteredOpen ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            )}
            Mastered
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{mastered.length}</span>
          </button>
          {masteredOpen && (
            <div className="space-y-2 border-t px-4 pb-4 pt-2">
              {(sortMode === 'dateAdded'
                ? [...groupWordsByDateAdded(mastered).values()].flat()
                : [...mastered].sort((a, b) =>
                    a.word.localeCompare(b.word, undefined, { sensitivity: 'base' }),
                  )
              ).map((w) => (
                <WordCard
                  key={w.id}
                  userWord={w}
                  onDelete={onDelete}
                  deleteAriaLabel={deleteAriaLabel}
                  draggable={enableDrag}
                  onAddToCollection={onAddToCollection}
                  searchQuery={searchQuery}
                  variant="list"
                  className="opacity-80"
                  highlighted={highlightWordId === w.id}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  )
}
